// React composition for the ported V2 betting surfaces: one tote adapter,
// one placement machine and one race-coupons read model shared by the
// onsite racecard/rail mount and the fullscreen shell. Money truth lives
// server-side (coupons + wallet); everything here is presentation state
// plus the idempotent placement calls from ticketPlacement.ts.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ticketKey } from "@renderer/odds.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api, type Racecard } from "@/api";
import { placeCouponRequest } from "@/betslip/controller";

import {
  createHrwTote,
  type ToteAdapter,
  type ToteField,
  type ToteLine,
} from "./hrwTote";
import {
  checkTicketStatus,
  clearForeignPendingTickets,
  clearPendingTicket,
  loadPendingTicket,
  type PlacedLine,
  placeTicket,
  savePendingTicket,
} from "./ticketPlacement";

export const MIN_BET_CENTS = 100;
const UNIT_STAKE_KEY = "hw.v2.unit-stake.v1";

export type MarketData = Awaited<ReturnType<typeof api.markets>>;

export function toteFieldFromCard(
  card: Racecard | null | undefined,
): ToteField {
  if (!card) return null;
  return {
    horses: card.runners.map((runner) => ({
      gate: runner.gate,
      name: runner.name,
      weight: runner.weight,
      silk: runner.silk,
      bloodline: runner.bloodline,
      appearance: runner.appearance,
    })),
  };
}

function loadUnitStake(): number {
  try {
    const cents = Number(window.localStorage.getItem(UNIT_STAKE_KEY));
    if (Number.isSafeInteger(cents) && cents >= MIN_BET_CENTS) return cents;
  } catch {
    // Sandboxed storage.
  }
  return 500;
}

// The unit stake is one session-wide value shared by the onsite panel and
// the fullscreen HUD (the donor's chip is likewise global to the game).
let unitStakeCents: number | null = null;

function getUnitStakeCents(): number {
  if (unitStakeCents === null) unitStakeCents = loadUnitStake();
  return unitStakeCents;
}

function setUnitStakeCents(cents: number): void {
  if (!Number.isSafeInteger(cents) || cents < MIN_BET_CENTS) return;
  unitStakeCents = cents;
  try {
    window.localStorage.setItem(UNIT_STAKE_KEY, String(cents));
  } catch {
    // Sandboxed storage.
  }
}

/** One tote adapter per surface lifetime, kept in sync with the racecard
 * field and the market's pricing basis / betting window. */
export function useV2Tote({
  card,
  market,
}: {
  card: Racecard | null | undefined;
  market: MarketData | null | undefined;
}) {
  const [tote] = useState<ToteAdapter>(() =>
    createHrwTote({ getChip: getUnitStakeCents }),
  );
  const fieldRaceRef = useRef<string | null>(null);

  useEffect(() => {
    if (!card) return;
    const sameRace = fieldRaceRef.current === card.raceId;
    fieldRaceRef.current = card.raceId;
    tote.setField(toteFieldFromCard(card), { keepTicket: sameRace });
  }, [card, tote]);

  useEffect(() => {
    if (market?.rtpBp) tote.setConfig({ rtpBp: market.rtpBp });
  }, [market?.rtpBp, tote]);

  useEffect(() => {
    tote.setLocked(!(market?.bettingOpen ?? false));
  }, [market?.bettingOpen, tote]);

  return {
    tote,
    getUnitStake: getUnitStakeCents,
    setUnitStake: setUnitStakeCents,
  };
}

// ---------------------------------------------------------------- coupons

type RaceCoupon = {
  couponId: string;
  status: string;
  result: string | null;
  returnedCents: number | null;
  stakeCents: number;
  placedAt: string | null;
  line: ToteLine;
};

function parseRaceCoupons(
  raw: Record<string, unknown> | undefined,
  raceId: string,
): RaceCoupon[] {
  const rows = Array.isArray(raw?.coupons)
    ? (raw.coupons as Array<Record<string, unknown>>)
    : [];
  const parsed: RaceCoupon[] = [];
  for (const row of rows) {
    const legs = Array.isArray(row.legs)
      ? (row.legs as Array<Record<string, unknown>>)
      : [];
    // The V2 ticket is single-race, single-leg by construction. Multi-leg
    // coupons belong to the BetSlip drawer and stay out of this HUD.
    if (row.type !== "single" || legs.length !== 1) continue;
    const leg = legs[0];
    if (leg.raceId !== raceId || row.status === "rejected") continue;
    const betType = typeof leg.betType === "string" ? leg.betType : "";
    const sel = Array.isArray(leg.sel) ? (leg.sel as number[]) : [];
    const stakeCents = Number(row.stakeCents ?? 0);
    parsed.push({
      couponId: typeof row.couponId === "string" ? row.couponId : "",
      status: typeof row.status === "string" ? row.status : "open",
      result: typeof row.result === "string" ? row.result : null,
      returnedCents:
        typeof row.returnedCents === "number" ? row.returnedCents : null,
      stakeCents,
      placedAt: typeof row.placedAt === "string" ? row.placedAt : null,
      line: {
        key: ticketKey(betType, sel),
        type: betType,
        sel,
        amount: stakeCents,
        oddsH: Number(leg.oddsH ?? 0),
      },
    });
  }
  return parsed;
}

/** Pure projection of the myCoupons payload for one race — exported so the
 * fullscreen mirror's guarantees are unit-testable without the query. */
export function raceCouponsModel(
  raw: Record<string, unknown> | undefined,
  raceId: string | null,
) {
  const coupons = raceId ? parseRaceCoupons(raw, raceId) : [];
  const settled = coupons.filter((coupon) => coupon.status === "settled");
  const open = coupons.filter((coupon) => coupon.status !== "settled");
  const stakeCents = coupons.reduce(
    (sum, coupon) => sum + coupon.stakeCents,
    0,
  );
  const returnedCents = settled.reduce(
    (sum, coupon) => sum + (coupon.returnedCents ?? 0),
    0,
  );
  // The earliest coupon anchors the ticket's durable receipt identity.
  const anchor = coupons
    .filter((coupon) => coupon.couponId)
    .sort((left, right) =>
      (left.placedAt ?? "").localeCompare(right.placedAt ?? ""),
    )[0];
  return {
    coupons,
    acceptedLines: coupons.map((coupon) => coupon.line),
    settledLines: coupons.map((coupon) => ({
      ...coupon.line,
      returnCents: coupon.status === "settled" ? coupon.returnedCents : null,
    })),
    stakeCents,
    returnedCents,
    hasTicket: coupons.length > 0,
    settlementPending: open.length > 0,
    allSettled: coupons.length > 0 && open.length === 0,
    reference: anchor?.couponId ?? null,
    placedAt: anchor?.placedAt ?? null,
    couponIds: coupons
      .map((coupon) => coupon.couponId)
      .filter((couponId) => couponId.length > 0),
  };
}

/** Authoritative read model for this race's own single-line coupons —
 * accepted ticket during the race, settled returns afterwards. */
export function useRaceCoupons({
  raceId,
  playerId,
  settling,
}: {
  raceId: string | null;
  playerId: number | null;
  settling: boolean;
}) {
  const query = useQuery({
    queryKey: ["bets", "race", raceId, playerId],
    queryFn: () => api.myCoupons(),
    enabled: Boolean(raceId && playerId !== null),
    // Poll only while a settlement for THIS race is actually outstanding.
    refetchInterval: (current) => {
      if (!settling || !raceId) return false;
      const parsed = parseRaceCoupons(current.state.data, raceId);
      const outstanding = parsed.some((coupon) => coupon.status !== "settled");
      return parsed.length && outstanding ? 3_000 : false;
    },
    staleTime: 5_000,
  });

  return useMemo(
    () => ({
      ...raceCouponsModel(query.data, raceId),
      refetch: query.refetch,
    }),
    [query.data, query.refetch, raceId],
  );
}

// -------------------------------------------------------------- placement

export type PlacementState = {
  transaction:
    "draft" | "submitting" | "accepted" | "rejected" | "status-unknown";
  message: string;
  accepted: PlacedLine[];
  stakeCents: number;
  revision: number;
  raceId: string | null;
  submissionId: string | null;
  acceptedAt: string;
};

const DRAFT_STATE: PlacementState = {
  transaction: "draft",
  message: "",
  accepted: [],
  stakeCents: 0,
  revision: 0,
  raceId: null,
  submissionId: null,
  acceptedAt: "",
};

export function placementAfterToteChange(
  current: PlacementState,
  reason: string,
): PlacementState {
  return current.transaction === "rejected" &&
    (reason === "lines" || reason === "market")
    ? DRAFT_STATE
    : current;
}

function acceptedSummary(
  outcome: { accepted: unknown[]; rejected: unknown[]; unknown: unknown[] },
  stakeCents: number,
  formatAmount: (cents: number) => string,
  fallback: string,
): string {
  if (
    outcome.accepted.length &&
    !outcome.rejected.length &&
    !outcome.unknown.length
  ) {
    const count = outcome.accepted.length;
    return `Accepted · ${count} bet${count === 1 ? "" : "s"} · ${formatAmount(stakeCents)}`;
  }
  return fallback;
}

/** The V2 transaction machine: draft → submitting → accepted | rejected |
 * status-unknown, with account-scoped pending recovery and rebet memory. */
export function useTicketPlacement({
  raceId,
  playerId,
  tote,
  formatAmount = (cents: number) => `$${(cents / 100).toFixed(2)}`,
}: {
  raceId: string | null;
  playerId: number | null;
  tote: ToteAdapter;
  formatAmount?: (cents: number) => string;
}) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<PlacementState>(DRAFT_STATE);
  const [lastTicket, setLastTicket] = useState<ToteLine[] | null>(null);
  const busyRef = useRef(false);
  const recoveredPlayerRef = useRef<number | null>(null);

  // A different race starts from draft; the previous race's coupons stay
  // authoritative server-side and render from useRaceCoupons. This is the
  // React render-phase adjustment pattern — never an effect.
  if (state.raceId && state.raceId !== raceId) {
    setState(DRAFT_STATE);
  }

  // A refusal remains visible until the player changes the draft. The next
  // pick/market action is a fresh intent and must not stay trapped behind a
  // stale "Betting closed" transaction from the previous attempt.
  useEffect(
    () =>
      tote.subscribe(
        (_snapshot, reason) =>
          setState((current) => placementAfterToteChange(current, reason)),
        { emitCurrent: false },
      ),
    [tote],
  );

  // Account-scoped recovery: another player's pending ticket is discarded;
  // this player's pending ticket surfaces as status-unknown for THIS race
  // and is never auto-replayed. The prompt lands on the next microtask so
  // the storage sweep itself stays render-free.
  useEffect(() => {
    if (playerId === null || recoveredPlayerRef.current === playerId) return;
    recoveredPlayerRef.current = playerId;
    clearForeignPendingTickets(playerId);
    const pending = loadPendingTicket(playerId);
    if (!pending || pending.raceId !== raceId) return;
    queueMicrotask(() => {
      setState({
        ...DRAFT_STATE,
        transaction: "status-unknown",
        message:
          "Your last bets were not confirmed. Check status to reconcile.",
        raceId: pending.raceId,
        submissionId: pending.submissionId,
      });
    });
  }, [playerId, raceId]);

  const place = useCallback(
    async (lines?: ToteLine[]) => {
      const ticket = lines?.length ? lines : tote.getLines();
      if (!raceId || playerId === null || !ticket.length || busyRef.current) {
        return;
      }
      busyRef.current = true;
      const submissionId = crypto.randomUUID();
      savePendingTicket(playerId, {
        submissionId,
        raceId,
        lines: ticket,
        createdAt: new Date().toISOString(),
      });
      setState((current) => ({
        ...current,
        transaction: "submitting",
        message: "",
        raceId,
        submissionId,
      }));
      try {
        const outcome = await placeTicket({
          raceId,
          lines: ticket,
          submissionId,
          place: (request, clientRef) =>
            placeCouponRequest(queryClient, request, clientRef),
        });
        if (outcome.transaction !== "status-unknown") {
          clearPendingTicket(playerId);
        }
        if (outcome.accepted.length) {
          setLastTicket(ticket);
          tote.clear();
        }
        const stakeCents = outcome.accepted.reduce(
          (sum, line) => sum + line.amount,
          0,
        );
        setState((current) => ({
          transaction: outcome.transaction,
          message: acceptedSummary(
            outcome,
            stakeCents,
            formatAmount,
            outcome.message,
          ),
          accepted: outcome.accepted,
          stakeCents,
          revision: current.revision + 1,
          raceId,
          submissionId,
          acceptedAt: new Date().toLocaleTimeString(),
        }));
        // The acceptance receipt holds a few seconds, then the surface
        // returns to a fresh draft while the window is still open (the
        // donor auto-advances to the next card here; HRW betting windows
        // stay open for minutes and the accepted ticket lives on in the
        // coupons read model and the fullscreen HUD's lock re-assert).
        if (outcome.transaction === "accepted") {
          window.setTimeout(() => {
            setState((current) =>
              current.submissionId === submissionId &&
              current.transaction === "accepted"
                ? DRAFT_STATE
                : current,
            );
          }, 3_500);
        }
      } finally {
        busyRef.current = false;
      }
    },
    [formatAmount, playerId, queryClient, raceId, tote],
  );

  const checkStatus = useCallback(async () => {
    if (playerId === null || busyRef.current) return;
    const pending = loadPendingTicket(playerId);
    if (!pending) {
      setState((current) =>
        current.transaction === "status-unknown"
          ? { ...current, transaction: "draft", message: "" }
          : current,
      );
      return;
    }
    busyRef.current = true;
    setState((current) => ({
      ...current,
      transaction: "submitting",
      message: "Checking bet status…",
    }));
    try {
      const outcome = await checkTicketStatus({
        raceId: pending.raceId,
        lines: pending.lines,
        submissionId: pending.submissionId,
        place: (request, clientRef) =>
          placeCouponRequest(queryClient, request, clientRef),
      });
      if (outcome.transaction !== "status-unknown") {
        clearPendingTicket(playerId);
      }
      if (outcome.accepted.length) {
        setLastTicket(pending.lines);
        tote.clear();
      }
      const stakeCents = outcome.accepted.reduce(
        (sum, line) => sum + line.amount,
        0,
      );
      setState((current) => ({
        transaction: outcome.transaction,
        message: acceptedSummary(
          outcome,
          stakeCents,
          formatAmount,
          outcome.message,
        ),
        accepted: outcome.accepted,
        stakeCents,
        revision: current.revision + 1,
        raceId: pending.raceId,
        submissionId: pending.submissionId,
        acceptedAt: new Date().toLocaleTimeString(),
      }));
      if (outcome.transaction === "accepted") {
        window.setTimeout(() => {
          setState((current) =>
            current.submissionId === pending.submissionId &&
            current.transaction === "accepted"
              ? DRAFT_STATE
              : current,
          );
        }, 3_500);
      }
    } finally {
      busyRef.current = false;
    }
  }, [formatAmount, playerId, queryClient, tote]);

  const rebet = useCallback(() => {
    const last = lastTicket;
    if (!last?.length) return;
    void tote.commands.addLines(
      last.map((line) => ({
        type: line.type,
        sel: line.sel,
        amount: line.amount,
        groupId: line.groupId,
        groupLabel: line.groupLabel,
      })),
    );
  }, [lastTicket, tote]);

  const resetToDraft = useCallback(() => {
    setState(DRAFT_STATE);
  }, []);

  return {
    state,
    place,
    checkStatus,
    rebet,
    resetToDraft,
    canRebet: Boolean(lastTicket?.length),
  };
}
